import { useCallback } from 'react';
import { TimekeeperService } from 'services/TimekeeperService';
import { WorkRule, WorkRuleGeneralInput } from 'repository/timekeeper/@types/WorkRuleModel';
import { SnackbarMessage } from 'notistack';

interface AlertMessage {
    title: string;
    message: SnackbarMessage;
    severity: 'error' | 'success' | 'warning' | 'info';
}

type SetLoading = (loading: boolean) => void;
type Callback = () => void;
type HandlerAlert = (message: AlertMessage) => void;

const timekeeperService = new TimekeeperService();

export const useWorkRuleActions = () => {
    const addWorkRule = useCallback(
        async ({
            setLoading,
            body,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            body: WorkRule;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            const newBody: WorkRuleGeneralInput = {
                name: body.name,
                effective_date: '3000-01-01',
                description: body.description || undefined,
                excepcion_rule_id: body.excepcion_rule_id || undefined,
                paycodes_distribution_id: body.paycodes_distribution_id || undefined,
                rounding_rule_id: body.rounding_rule_id || undefined,
                bonus: body.bonus?.map((item) => (typeof item === 'object' ? (item as any).id : item)) ?? undefined,
                break_rules: body.break_rules?.map((item) => (typeof item === 'object' ? (item as any).id : item)) ?? undefined,
                overtime_rules: body.overtime_rules?.map((item) => (typeof item === 'object' ? (item as any).id : item)) ?? undefined,
                zones: body.zones?.map((item) => (typeof item === 'object' ? (item as any).id : item)) ?? undefined,
                schedule_desviation_rules: body.schedule_desviation_rules?.map((item) => (typeof item === 'object' ? (item as any).id : item)) ?? undefined,
                automatic_break: body.automatic_break ?? undefined,
            };

            try {
                await timekeeperService.work_rules.createWorkRule(newBody);
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const updateWorkRule = useCallback(
        async ({
            setLoading,
            workRuleId,
            body,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            workRuleId: number;
            body: WorkRule;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            const newBody: WorkRuleGeneralInput = {
                name: body.name,
                effective_date: body.effective_date ? body.effective_date : '3000-01-01',
                description: body.description || undefined,
                excepcion_rule_id: body.excepcion_rule_id || undefined,
                paycodes_distribution_id: body.paycodes_distribution_id || undefined,
                rounding_rule_id: body.rounding_rule_id || undefined,
                bonus: body.bonus?.map((item) => (typeof item === 'object' ? (item as any).id : item)) ?? undefined,
                break_rules: body.break_rules?.map((item) => (typeof item === 'object' ? (item as any).id : item)) ?? undefined,
                overtime_rules: body.overtime_rules?.map((item) => (typeof item === 'object' ? (item as any).id : item)) ?? undefined,
                zones: body.zones?.map((item) => (typeof item === 'object' ? (item as any).id : item)) ?? undefined,
                schedule_desviation_rules: body.schedule_desviation_rules?.map((item) => (typeof item === 'object' ? (item as any).id : item)) ?? undefined,
                automatic_break: body.automatic_break ?? undefined,
            };

            try {
                await timekeeperService.work_rules.updateWorkRule(workRuleId, newBody);
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const deleteWorkRules = useCallback(
        async ({
            setLoading,
            workRuleIds,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            workRuleIds: number[];
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            try {
                await Promise.all(workRuleIds.map((workRuleId) => timekeeperService.work_rules.deleteWorkRule(workRuleId)));
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    return {
        addWorkRule,
        updateWorkRule,
        deleteWorkRules,
    };
};
